import React, {useCallback, useState} from 'react';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    selectDoor,
    selectEdge,
    selectHasDoors,
    selectLoadingMatchingEdge,
    selectLoadingNewColour,
    selectMaterial,
    selectedMenuSet,
} from 'components/customer/AdvancedMaterials/store/materialSlice';
import {shallowEqual} from 'react-redux';
import {
    Title,
    TitleContainer,
} from 'components/customer/AdvancedMaterials/Doors/MaterialType';
import styled from 'styled-components';
import {
    DoorImage,
    Name,
} from 'components/customer/AdvancedMaterials/Doors/Door';
import {
    ImageContainer,
    MaterialImage,
    SubContainer,
    Name as MaterialName,
} from 'components/customer/AdvancedMaterials/Materials/Material';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {Menu} from 'components/customer/AdvancedMaterials/entity/Menu';
import {getCabinetFields, Icon} from 'shared/helpers';
import {
    Material as OGMaterial,
    MaterialEdge,
} from 'components/customer/Materials/entity';
import {NewSelectionPopover} from 'components/customer/AdvancedMaterials/CurrentSelections/NewSelectionPopover';
import {Material} from 'components/customer/AdvancedMaterials/entity/Material';
import {useSelectedMenu} from 'components/customer/AdvancedMaterials/helpers/useSelectedMenu';
import {ProductContextType} from 'components/customer/Product/Specs';
import {useProductContext} from 'contexts';
import {mobile} from 'shared/Breakpoints';
import {isMobile, isTablet} from 'react-device-detect';

export const MaterialAndDoorSelection = () => {
    const dispatch = useAppDispatch();
    const material = useAppSelector(
        selectMaterial,
        shallowEqual
    ) as Material<OGMaterial>;
    const door = useAppSelector(selectDoor, shallowEqual);
    const edge = useAppSelector(
        selectEdge,
        shallowEqual
    ) as Material<MaterialEdge>;

    const hasDoor = useAppSelector(selectHasDoors);
    const loadingNewColour = useAppSelector(selectLoadingNewColour);
    const loadingMatchingEdge = useAppSelector(selectLoadingMatchingEdge);
    const {selectedMenu} = useSelectedMenu();
    const {formStructure} = useProductContext<ProductContextType>();
    const [doorZoomed, setDoorZoomed] = useState(false);
    const [materialZoomed, setMaterialZoomed] = useState(false);
    const [edgeZoomed, setEdgeZoomed] = useState(false);

    let showEdgeMaterials = true;

    const zoomAllowed = !Boolean(isMobile || isTablet);

    if (Array.isArray(formStructure) && formStructure.length > 0) {
        const materialFields = getCabinetFields(formStructure) as {
            hasEdgeMaterial: boolean;
        };

        showEdgeMaterials = materialFields?.hasEdgeMaterial;
    }

    // Door
    const handleDoorZoom = useCallback(
        (value?: boolean) => {
            if (!zoomAllowed) return;

            if (typeof value === 'boolean') {
                setDoorZoomed(value);
            } else {
                setDoorZoomed((prev) => !prev);
            }
        },
        [zoomAllowed]
    );

    const handleMouseLeaveDoor = useCallback(() => {
        handleDoorZoom(false);
    }, [handleDoorZoom]);

    const handleZoomClickDoor = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            handleDoorZoom();
        },
        [handleDoorZoom]
    );

    const onClickDoor = useCallback(() => {
        dispatch(selectedMenuSet(Menu.DOOR_TYPE, true));
    }, []);

    // Material
    const handleMaterialZoom = useCallback(
        (value?: boolean) => {
            if (!zoomAllowed) return;

            if (typeof value === 'boolean') {
                setMaterialZoomed(value);
            } else {
                setMaterialZoomed((prev) => !prev);
            }
        },
        [zoomAllowed]
    );

    const handleMouseLeaveMaterial = useCallback(() => {
        handleMaterialZoom(false);
    }, [handleMaterialZoom]);

    const handleZoomClickMaterial = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            handleMaterialZoom();
        },
        [handleMaterialZoom]
    );

    const onClickMaterial = useCallback(() => {
        dispatch(selectedMenuSet(Menu.MATERIAL, true));
    }, []);

    // Edge
    const handleEdgeZoom = useCallback(
        (value?: boolean) => {
            if (!zoomAllowed) return;

            if (typeof value === 'boolean') {
                setEdgeZoomed(value);
            } else {
                setEdgeZoomed((prev) => !prev);
            }
        },
        [dispatch, zoomAllowed]
    );

    const handleMouseLeaveEdge = useCallback(() => {
        handleEdgeZoom(false);
    }, [handleEdgeZoom]);

    const handleZoomClickEdge = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            handleEdgeZoom();
        },
        [handleEdgeZoom]
    );

    const onClickEdge = useCallback(() => {
        dispatch(selectedMenuSet(Menu.EDGE_MATERIAL, true));
    }, []);

    // Start of return statement.

    return (
        <>
            <LocalTitleContainer>
                <Title>New Selections</Title>
            </LocalTitleContainer>
            <Container>
                {hasDoor ? (
                    <LocalSubContainer
                        onClick={onClickDoor}
                        onMouseLeave={handleMouseLeaveDoor}
                        $selected={selectedMenu == Menu.DOOR_TYPE}
                        $dropdownOpen={false}
                        $previewOnly={true}
                        $zoomed={doorZoomed}>
                        <Label $selected={selectedMenu == Menu.DOOR_TYPE}>
                            <span>Door</span>
                            {zoomAllowed && door ? (
                                <ZoomIcon onClick={handleZoomClickDoor}>
                                    <Icon iconName="zoom.svg" title="Zoom in" />
                                </ZoomIcon>
                            ) : null}
                        </Label>
                        {doorZoomed ? (
                            <NewSelectionPopover
                                selected={selectedMenu == Menu.DOOR_TYPE}
                                image={door.image?.name ?? ''}
                                title="Door"
                                description={door.name ?? ''}
                                show={doorZoomed}
                            />
                        ) : (
                            <div style={{maxWidth: '200px'}}>
                                <LocalDoorImage
                                    src={door?.image?.name}
                                    potrait={true}
                                />
                                <Name>{door?.name}</Name>
                            </div>
                        )}
                    </LocalSubContainer>
                ) : null}
                <LocalSubContainer
                    onClick={onClickMaterial}
                    onMouseLeave={handleMouseLeaveMaterial}
                    $selected={selectedMenu === Menu.MATERIAL}
                    $dropdownOpen={false}
                    $previewOnly={true}
                    $zoomed={materialZoomed}>
                    <Label $selected={selectedMenu === Menu.MATERIAL}>
                        <span>Material</span>
                        {zoomAllowed && material ? (
                            <ZoomIcon onClick={handleZoomClickMaterial}>
                                <Icon iconName="zoom.svg" title="Zoom in" />
                            </ZoomIcon>
                        ) : null}
                    </Label>

                    {materialZoomed ? (
                        <NewSelectionPopover
                            selected={selectedMenu == Menu.MATERIAL}
                            image={material.image}
                            title="Material"
                            description={material.data.displayName}
                            show={materialZoomed}
                        />
                    ) : (
                        <div style={{maxWidth: '200px'}}>
                            <ImageContainer>
                                <MaterialImage src={material.image} />
                            </ImageContainer>
                            <MaterialName>
                                <span>
                                    <strong>{material.thickness}mm</strong>{' '}
                                    {parseHtmlString(material.name)}{' '}
                                    <em>{material.finish.name}</em>
                                </span>
                            </MaterialName>
                            {loadingNewColour ? (
                                <LocalIcon iconName="tail-spin.svg" />
                            ) : null}
                        </div>
                    )}
                </LocalSubContainer>

                {showEdgeMaterials ? (
                    <LocalSubContainer
                        onClick={onClickEdge}
                        onMouseLeave={handleMouseLeaveEdge}
                        $selected={selectedMenu == Menu.EDGE_MATERIAL}
                        $dropdownOpen={false}
                        $previewOnly={true}
                        $zoomed={edgeZoomed}>
                        <Label $selected={selectedMenu == Menu.EDGE_MATERIAL}>
                            <span>Edge</span>
                            {zoomAllowed && material ? (
                                <ZoomIcon onClick={handleZoomClickEdge}>
                                    <Icon iconName="zoom.svg" title="Zoom in" />
                                </ZoomIcon>
                            ) : null}
                        </Label>
                        {edgeZoomed ? (
                            <NewSelectionPopover
                                selected={selectedMenu == Menu.EDGE_MATERIAL}
                                image={edge.image}
                                title="Edge"
                                description={edge.data.displayName}
                                show={edgeZoomed}
                            />
                        ) : (
                            <div style={{maxWidth: '200px'}}>
                                <ImageContainer>
                                    <MaterialImage src={edge.image} />
                                </ImageContainer>
                                <MaterialName>
                                    <span>
                                        {edge.thickness > 0 ? (
                                            <strong>{edge.thickness}mm </strong>
                                        ) : null}
                                        {parseHtmlString(edge.name)}{' '}
                                        <em>{edge.finish.name}</em>
                                    </span>
                                </MaterialName>
                                {loadingMatchingEdge ? (
                                    <LocalIcon iconName="tail-spin.svg" />
                                ) : null}
                            </div>
                        )}
                    </LocalSubContainer>
                ) : null}
            </Container>
        </>
    );
};

const LocalIcon = styled((props: React.ComponentProps<typeof Icon>) => (
    <Icon {...props} />
))`
    width: 25px !important;
    position: absolute;
    bottom: 3px;
    right: 5px;
`;

const LocalDoorImage = styled(DoorImage)`
    width: 80px !important;
    height: 98px;
    gap: 5px;
`;

const LocalTitleContainer = styled(TitleContainer)`
    margin: 0;
`;

const Container = styled.section`
    display: flex;
    align-items: flex-start;
    margin: 5px 0;
    gap: 5px;
`;

const LocalSubContainer = styled(SubContainer)<{
    $zoomed: boolean;
}>`
    position: relative;
    transition:
        transform 0.3s ease,
        max-width 0.3s ease;

    > div > img {
        max-width: 100% !important;
        height: ${({$zoomed}) => ($zoomed ? 'auto' : '100px')};
        transition: height 0.3s ease; // Only transition height
    }

    min-height: 180px;
    ${mobile`
        transform: translateX(0) !important;

        > div > img {
            max-width: 100% !important;
            height: auto !important;
        }
    `}
`;

const Label = styled.div<{$selected: boolean}>`
    position: absolute;
    top: 0;
    left: 0;
    padding: 2px 10px;
    color: white;
    font-weight: bold;
    border-top-left-radius: 5px;
    border-bottom-right-radius: 5px;
    background: rgb(var(--secondary_colour));
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 5px;
    pointer-events: none;
    max-width: 400px;

    > div {
        pointer-events: auto;

        > img {
            width: 25px;
        }
    }

    ${({$selected}) =>
        $selected &&
        `
        background: rgb(var(--primary_colour));
    `}
`;

const ZoomIcon = styled.div`
    pointer-events: auto;
    cursor: pointer;

    > img {
        width: 25px;
    }
`;
